interface ParsedAddress {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

/**
 * Parses Google API address components into a standardized format
 * Works with both Google Places API and Geocoding API responses
 * @param addressComponents - Array of address components from Google API
 * @returns Parsed address object with street, city, state, zipCode, country
 */
export const parseGoogleAddressComponents = (
  addressComponents: AddressComponent[]
): Partial<ParsedAddress> => {
  if (!addressComponents || !Array.isArray(addressComponents)) {
    return {};
  }

  // Helper to find component by type
  const getComponent = (types: string[]) =>
    addressComponents.find(c => types.some(t => c.types.includes(t)));

  // Extract street number and route for street1
  const streetNumber = getComponent(['street_number'])?.long_name || '';
  const route = getComponent(['route'])?.long_name || '';
  const street1 = [streetNumber, route].filter(Boolean).join(' ');

  // Extract other components
  const city =
    getComponent(['locality'])?.long_name ||
    getComponent(['postal_town'])?.long_name ||
    getComponent(['administrative_area_level_2'])?.long_name ||
    '';

  const state = getComponent(['administrative_area_level_1'])?.long_name || '';

  const zipCode = getComponent(['postal_code'])?.long_name || '';

  const country = getComponent(['country'])?.long_name || '';

  return {
    street1,
    street2: '', // Google API doesn't typically provide street2
    city,
    state,
    zipCode,
    country,
  };
};

/**
 * Extracts place_id from Google Places API response
 * @param placeData - Place data from Google API
 * @returns place_id string or undefined
 */
export const extractPlaceId = (placeData: any): string | undefined => {
  return placeData?.place_id;
};
